import { toTaxonomySlug } from './subgenreRegistry'

/**
 * Content tags scoped to a SINGLE subgenre.
 *
 * The point of this file is editorial restraint: a reader on Sports Romance
 * should see "Hockey" and "Coach × Player", not the platform's entire tag
 * vocabulary. Each subgenre surfaces only the tags that genuinely narrow it.
 *
 * `category` reuses the platform's existing tag categories so colours and
 * iconography stay consistent with /all-content-tags.
 */
export type SubgenreTagCategory =
  | 'Tropes'
  | 'Settings'
  | 'Characters'
  | 'Themes'
  | 'Content Warnings'
  | 'Relationship Types'

export interface SubgenreContentTag {
  id: string
  name: string
  description: string
  count: number
  category: SubgenreTagCategory
}

const tag = (
  name: string,
  category: SubgenreTagCategory,
  description: string,
  count: number,
): SubgenreContentTag => ({
  id: toTaxonomySlug(name),
  name,
  description,
  count,
  category,
})

/** Keyed by subgenre slug (see toTaxonomySlug). */
export const SUBGENRE_CONTENT_TAGS: Record<string, SubgenreContentTag[]> = {
  'contemporary-romance': [
    tag('Second Chance', 'Tropes', 'Former partners finding their way back to each other.', 820),
    tag('Fake Dating', 'Tropes', 'A pretend relationship that stops being pretend.', 940),
    tag('Friends to Lovers', 'Tropes', 'A long friendship tipping over into romance.', 1120),
    tag('Single Parent', 'Characters', 'One lead is raising a child alone.', 460),
    tag('Grumpy × Sunshine', 'Tropes', 'A guarded lead met with relentless warmth.', 1080),
    tag('Slow Burn', 'Tropes', 'Attraction built over a long, deliberate arc.', 1340),
    tag('City Life', 'Settings', 'Urban settings shaping careers and chance meetings.', 610),
    tag('Second Act', 'Themes', 'Starting over in mid-life or after a career ends.', 380),
  ],
  'historical-romance': [
    tag('Regency', 'Settings', 'Early 19th-century England and its social season.', 1240),
    tag('Victorian', 'Settings', 'Industrial-era propriety, repression, and reform.', 680),
    tag('Highlander', 'Characters', 'Scottish Highland warriors and clan loyalty.', 520),
    tag('Arranged Marriage', 'Tropes', 'A union made by family or contract, not choice.', 890),
    tag('Marriage of Convenience', 'Tropes', 'A practical union that becomes genuine.', 760),
    tag('Forbidden Rank', 'Themes', 'Love across an impassable class divide.', 540),
    tag('Wallflower', 'Characters', 'An overlooked lead finally seen.', 430),
    tag('Rake Reformed', 'Tropes', 'A notorious libertine undone by real feeling.', 610),
  ],
  'romantic-comedy': [
    tag('Fake Dating', 'Tropes', 'A pretend relationship played for comedy.', 940),
    tag('Enemies to Lovers', 'Tropes', 'Antagonism sharpened into banter.', 1420),
    tag('Meet Cute', 'Tropes', 'An absurd or charming first encounter.', 720),
    tag('Forced Proximity', 'Tropes', 'Circumstances trapping two leads together.', 1010),
    tag('Wedding', 'Settings', 'Weddings as the engine of chaos and romance.', 480),
    tag('Workplace Hijinks', 'Settings', 'Office comedy driving the relationship.', 560),
    tag('Found Family', 'Themes', 'A chosen circle of friends as emotional anchor.', 690),
    tag('Banter Heavy', 'Tropes', 'Dialogue-forward, quick-witted romance.', 830),
  ],
  'romantic-suspense': [
    tag('Bodyguard', 'Characters', 'Protection duty turning personal.', 640),
    tag('Witness Protection', 'Tropes', 'Hidden identity under active threat.', 380),
    tag('On the Run', 'Tropes', 'Leads fleeing together while pursued.', 520),
    tag('Detective', 'Characters', 'An investigator drawn to a case-adjacent love interest.', 590),
    tag('Serial Killer', 'Content Warnings', 'An active killer as the central threat.', 340),
    tag('Cold Case', 'Tropes', 'An old unsolved crime reopened.', 290),
    tag('Forced Proximity', 'Tropes', 'Safehouses and stakeouts pushing leads together.', 1010),
    tag('Second Chance', 'Tropes', 'Former partners reunited by danger.', 820),
  ],
  'dark-romance': [
    tag('Morally Grey', 'Characters', 'A lead whose choices resist easy judgement.', 1180),
    tag('Enemies to Lovers', 'Tropes', 'Genuine hostility preceding attachment.', 1420),
    tag('Age Gap', 'Tropes', 'A significant difference in age and power.', 760),
    tag('Revenge', 'Themes', 'Retribution driving the plot and the pairing.', 690),
    tag('Possessive Hero', 'Tropes', 'Devotion expressed as territoriality.', 940),
    tag('Forced Marriage', 'Tropes', 'A union entered under coercion.', 480),
    tag('Violence', 'Content Warnings', 'On-page physical violence.', 1240),
    tag('Touch Her and Die', 'Tropes', 'Absolute protective retaliation.', 520),
  ],
  'pitch-black-romance': [
    tag('Non-Consensual', 'Content Warnings', 'Explicitly non-consensual content, central not incidental.', 310),
    tag('Dubious Consent', 'Content Warnings', 'Consent deliberately left ambiguous.', 440),
    tag('Captivity', 'Tropes', 'One lead held by the other for much of the book.', 380),
    tag('Obsession', 'Themes', 'Fixation as the primary emotional driver.', 520),
    tag('Psychological Manipulation', 'Content Warnings', 'Sustained coercive control and gaslighting.', 410),
    tag('Stalking', 'Content Warnings', 'Surveillance and pursuit of the love interest.', 350),
    tag('Blackmail', 'Tropes', 'Leverage and coercion binding the leads.', 280),
    tag('Extreme Possessiveness', 'Tropes', 'Ownership framed as devotion.', 400),
    tag('Revenge', 'Themes', 'Retribution pursued without limit.', 690),
  ],
  'taboo-romance': [
    tag('Age Gap', 'Tropes', 'A wide and deliberate age difference.', 760),
    tag('Step-Relations', 'Tropes', 'Romance between step-family members.', 520),
    tag('Forbidden', 'Themes', 'A pairing that breaks an explicit social rule.', 880),
    tag('Power Imbalance', 'Content Warnings', 'Structural inequality between the leads.', 640),
    tag('Authority Figure', 'Characters', 'A mentor, boss, or guardian as love interest.', 470),
    tag('Secret Relationship', 'Tropes', 'A romance that must stay hidden.', 590),
    tag('Cheating', 'Content Warnings', 'Infidelity on the page.', 320),
    tag('Why Choose', 'Relationship Types', 'More than two partners, no one rejected.', 410),
  ],
  'erotic-romance': [
    tag('Explicit', 'Content Warnings', 'Frequent, detailed intimate content.', 1420),
    tag('Praise Kink', 'Tropes', 'Affirmation as a central dynamic.', 480),
    tag('Slow Burn', 'Tropes', 'Extended tension before release.', 1340),
    tag('First Time', 'Tropes', 'A lead’s first sexual experience.', 390),
    tag('Why Choose', 'Relationship Types', 'Multiple partners without a final choice.', 410),
    tag('Workplace', 'Settings', 'Professional settings and their risks.', 560),
    tag('BDSM', 'Tropes', 'Negotiated power exchange.', 720),
    tag('Friends with Benefits', 'Tropes', 'A physical arrangement gaining feeling.', 610),
  ],
  'new-adult-romance': [
    tag('College', 'Settings', 'University life, coursework, and independence.', 910),
    tag('First Love', 'Themes', 'An early formative relationship.', 680),
    tag('Roommates', 'Tropes', 'Shared living quarters forcing intimacy.', 540),
    tag('Coming of Age', 'Themes', 'Becoming an adult alongside falling in love.', 780),
    tag('Found Family', 'Themes', 'A chosen support network away from home.', 690),
    tag('Self-Discovery', 'Themes', 'Identity worked out through relationship.', 620),
    tag('Mental Health', 'Content Warnings', 'Depression, anxiety, or therapy on the page.', 430),
    tag('Slow Burn', 'Tropes', 'A gradual, uncertain progression.', 1340),
  ],
  omegaverse: [
    tag('Alpha', 'Characters', 'A dominant presenting role in the hierarchy.', 620),
    tag('Omega', 'Characters', 'A receptive presenting role, often the protagonist.', 610),
    tag('Beta', 'Characters', 'The unaligned middle of the hierarchy.', 280),
    tag('Fated Mates', 'Tropes', 'A biologically destined pairing.', 740),
    tag('Heat Cycle', 'Tropes', 'Biology forcing proximity and urgency.', 580),
    tag('Scent Bonding', 'Tropes', 'Recognition and attachment through scent.', 490),
    tag('Mpreg', 'Tropes', 'Male pregnancy within the presenting system.', 340),
    tag('Pack Dynamics', 'Relationship Types', 'Group hierarchy and multi-partner bonds.', 420),
    tag('Knotting', 'Content Warnings', 'Explicit omegaverse-specific anatomy.', 380),
  ],
  'small-town-romance': [
    tag('Farm', 'Settings', 'Working farmland and its seasonal rhythm.', 420),
    tag('Ranch', 'Settings', 'Cattle country, horses, and hard labour.', 480),
    tag('Coastal Town', 'Settings', 'Harbours, tides, and off-season quiet.', 390),
    tag('Sheriff', 'Characters', 'Local law enforcement as love interest.', 310),
    tag('Returning Home', 'Tropes', 'A lead coming back after years away.', 640),
    tag('Local Business', 'Settings', 'Bakeries, bookshops, and bars as anchors.', 520),
    tag('Found Family', 'Themes', 'The town itself as chosen family.', 690),
    tag('Festival', 'Settings', 'Fairs and seasonal events driving the plot.', 280),
  ],
  'sports-romance': [
    tag('Hockey', 'Settings', 'Rink culture, road trips, and playoff pressure.', 680),
    tag('Rugby', 'Settings', 'Club rugby and its physical toll.', 240),
    tag('Formula 1', 'Settings', 'Motorsport paddocks and championship stakes.', 290),
    tag('Tennis', 'Settings', 'Individual competition and ranking pressure.', 180),
    tag('Soccer', 'Settings', 'Football clubs, transfers, and supporters.', 320),
    tag('Basketball', 'Settings', 'Court culture and draft-year stakes.', 260),
    tag('Coach × Player', 'Tropes', 'A relationship across a clear authority line.', 340),
    tag('Teammates', 'Tropes', 'Romance inside the same locker room.', 410),
    tag('Rivals', 'Tropes', 'Competitors on opposing sides.', 520),
    tag('College Sports', 'Settings', 'University athletics and scholarship pressure.', 380),
  ],
  'billionaire-romance': [
    tag('CEO', 'Characters', 'A lead running the company.', 780),
    tag('Boss × Assistant', 'Tropes', 'Romance across a reporting line.', 590),
    tag('Marriage of Convenience', 'Tropes', 'A union serving a business purpose.', 760),
    tag('Power Imbalance', 'Content Warnings', 'Wealth as structural leverage.', 640),
    tag('Fake Fiancée', 'Tropes', 'A staged engagement for appearances.', 430),
    tag('Luxury', 'Settings', 'Private jets, penthouses, and old money.', 520),
    tag('Class Divide', 'Themes', 'Money separating the leads socially.', 480),
    tag('Secret Baby', 'Tropes', 'A child unknown to one parent.', 350),
  ],
  'mafia-romance': [
    tag('Arranged Marriage', 'Tropes', 'A union sealing an alliance between families.', 890),
    tag('Captive', 'Tropes', 'One lead held as leverage or prize.', 380),
    tag('Morally Grey', 'Characters', 'A lead whose violence is never excused away.', 1180),
    tag('Revenge', 'Themes', 'Vendetta driving the plot.', 690),
    tag('Bratva', 'Settings', 'Russian organised crime structures.', 320),
    tag('Enemies to Lovers', 'Tropes', 'Rival families, genuine hostility.', 1420),
    tag('Violence', 'Content Warnings', 'On-page brutality and killing.', 1240),
    tag('Loyalty', 'Themes', 'Family obligation set against desire.', 540),
  ],
  'mc-romance': [
    tag('Club Life', 'Settings', 'Clubhouse hierarchy and patched membership.', 310),
    tag('Old Lady', 'Characters', 'A claimed partner within club culture.', 240),
    tag('Brotherhood', 'Themes', 'Loyalty to the club above all else.', 380),
    tag('Territory', 'Themes', 'Turf conflict driving the stakes.', 260),
    tag('Forbidden', 'Themes', 'A pairing the club forbids.', 880),
    tag('Violence', 'Content Warnings', 'On-page violence and retaliation.', 1240),
    tag('Protector', 'Tropes', 'Sheltering someone under club protection.', 420),
    tag('Outlaw', 'Characters', 'A lead living outside the law by choice.', 290),
  ],
  'military-romance': [
    tag('Deployment', 'Tropes', 'Separation and the wait for return.', 380),
    tag('Homecoming', 'Tropes', 'Reintegration after service.', 340),
    tag('Navy SEAL', 'Characters', 'Special operations background.', 420),
    tag('PTSD', 'Content Warnings', 'Combat trauma addressed on the page.', 460),
    tag('Brothers in Arms', 'Themes', 'Unit loyalty as an emotional spine.', 290),
    tag('Second Chance', 'Tropes', 'Reunion after a service-driven separation.', 820),
    tag('Duty vs Desire', 'Themes', 'Service obligation against personal want.', 380),
    tag('Single Parent', 'Characters', 'Raising a child between deployments.', 460),
  ],
  'workplace-romance': [
    tag('Boss × Assistant', 'Tropes', 'Romance across a reporting line.', 590),
    tag('Office Rivals', 'Tropes', 'Competition for the same promotion.', 480),
    tag('Coworkers', 'Tropes', 'Peers navigating a workplace relationship.', 620),
    tag('Forced Proximity', 'Tropes', 'Travel and late nights pushing leads together.', 1010),
    tag('Secret Relationship', 'Tropes', 'A romance hidden from HR.', 590),
    tag('Power Imbalance', 'Content Warnings', 'Seniority as leverage.', 640),
    tag('Second Chance', 'Tropes', 'A past relationship resurfacing at work.', 820),
    tag('Grumpy × Sunshine', 'Tropes', 'Opposed temperaments forced to collaborate.', 1080),
  ],
  'college-romance': [
    tag('Roommates', 'Tropes', 'Shared housing forcing intimacy.', 540),
    tag('Campus Life', 'Settings', 'Lectures, dorms, and student politics.', 680),
    tag('Sorority', 'Settings', 'Greek life and its hierarchies.', 240),
    tag('Academic Rivals', 'Tropes', 'Competition for standing and grades.', 380),
    tag('First Love', 'Themes', 'A formative early relationship.', 680),
    tag('Athlete', 'Characters', 'A student athlete balancing sport and study.', 420),
    tag('Coming of Age', 'Themes', 'Growing up over the course of the book.', 780),
    tag('Found Family', 'Themes', 'A chosen circle away from home.', 690),
  ],
  'monster-romance': [
    tag('Orc', 'Characters', 'Tusked, warrior-coded non-human leads.', 340),
    tag('Dragon', 'Characters', 'Shifting or fully draconic partners.', 480),
    tag('Vampire', 'Characters', 'Blood-drinking immortals.', 720),
    tag('Werewolf', 'Characters', 'Lycanthropes and pack structure.', 690),
    tag('Kraken', 'Characters', 'Deep-sea leviathan partners.', 160),
    tag('Minotaur', 'Characters', 'Bull-formed non-human leads.', 180),
    tag('Tentacles', 'Content Warnings', 'Explicit non-human anatomy.', 260),
    tag('Monster Society', 'Settings', 'Worlds where monsters have their own civilisation.', 310),
  ],
  'alien-romance': [
    tag('Abduction', 'Tropes', 'Taken from Earth, willingly or not.', 320),
    tag('First Contact', 'Themes', 'Initial meeting between species.', 380),
    tag('Fated Mates', 'Tropes', 'A biologically destined bond.', 740),
    tag('Breeding Programme', 'Content Warnings', 'Reproduction as plot obligation.', 240),
    tag('Alien Anatomy', 'Content Warnings', 'Explicit non-human physiology.', 280),
    tag('Language Barrier', 'Tropes', 'Communication earned rather than assumed.', 260),
    tag('Warrior Species', 'Characters', 'A martial non-human culture.', 340),
    tag('Stranded', 'Tropes', 'Marooned together off-world.', 290),
  ],
  'stalker-romance': [
    tag('Surveillance', 'Content Warnings', 'Watching and tracking the love interest.', 280),
    tag('Obsession', 'Themes', 'Fixation as the engine of the plot.', 520),
    tag('Unhinged Hero', 'Characters', 'A lead whose devotion has no brakes.', 340),
    tag('Breaking and Entering', 'Content Warnings', 'Invasion of the protagonist’s home.', 190),
    tag('Extreme Possessiveness', 'Tropes', 'Ownership framed as love.', 400),
    tag('Dubious Consent', 'Content Warnings', 'Consent left deliberately ambiguous.', 440),
    tag('Hidden Identity', 'Tropes', 'A pursuer known under another name.', 310),
  ],
  'captive-romance': [
    tag('Captivity', 'Tropes', 'Confinement across most of the book.', 380),
    tag('Kidnapping', 'Content Warnings', 'Abduction as the inciting event.', 340),
    tag('Captor × Captive', 'Tropes', 'The central power dynamic.', 360),
    tag('Stockholm Syndrome', 'Content Warnings', 'Attachment formed under captivity.', 280),
    tag('Escape Attempt', 'Tropes', 'Repeated bids for freedom.', 240),
    tag('Dubious Consent', 'Content Warnings', 'Consent complicated by confinement.', 440),
    tag('Slow Corruption', 'Themes', 'Gradual erosion of resistance.', 260),
  ],
  'psychological-romance': [
    tag('Unreliable Narrator', 'Tropes', 'A perspective the reader cannot trust.', 420),
    tag('Gaslighting', 'Content Warnings', 'Reality systematically undermined.', 380),
    tag('Obsession', 'Themes', 'Fixation over external plot.', 520),
    tag('Mind Games', 'Tropes', 'Manipulation as courtship.', 340),
    tag('Identity', 'Themes', 'Selfhood destabilised by the relationship.', 460),
    tag('Mental Health', 'Content Warnings', 'Clinical conditions on the page.', 430),
    tag('Morally Grey', 'Characters', 'Neither lead is clearly right.', 1180),
  ],
  'fantasy-romance': [
    tag('Fated Mates', 'Tropes', 'A bond written into the world’s magic.', 740),
    tag('Court Intrigue', 'Settings', 'Political manoeuvring among the powerful.', 620),
    tag('Fae', 'Characters', 'Bargain-bound immortals of the courts.', 810),
    tag('Chosen One', 'Tropes', 'A destiny placed on one lead.', 690),
    tag('Enemies to Lovers', 'Tropes', 'Opposed sides of a magical conflict.', 1420),
    tag('Magic System', 'Themes', 'Rule-bound magic shaping the romance.', 580),
    tag('Dragon Rider', 'Characters', 'Bonded human and dragon partnerships.', 340),
    tag('Trials', 'Tropes', 'A competition or ordeal structuring the plot.', 420),
  ],
  'sci-fi-romance': [
    tag('Space Station', 'Settings', 'Confined orbital or deep-space habitats.', 280),
    tag('Fated Mates', 'Tropes', 'A biologically or technologically destined bond.', 740),
    tag('Cyborg', 'Characters', 'Augmented or part-machine leads.', 320),
    tag('Generation Ship', 'Settings', 'Long-haul vessels and their societies.', 190),
    tag('Arranged Alliance', 'Tropes', 'A union sealing an interstellar treaty.', 240),
    tag('First Contact', 'Themes', 'Meeting a new species.', 380),
    tag('Stranded', 'Tropes', 'Marooned together off-world.', 290),
    tag('AI Companion', 'Characters', 'An artificial intelligence in the relationship.', 210),
  ],
  'paranormal-romance': [
    tag('Vampire', 'Characters', 'Blood-drinking immortals.', 720),
    tag('Werewolf', 'Characters', 'Shifters and pack hierarchy.', 690),
    tag('Witch', 'Characters', 'Practitioners of ritual magic.', 540),
    tag('Fated Mates', 'Tropes', 'A supernatural destined bond.', 740),
    tag('Shifter', 'Characters', 'Animal-shifting leads of any species.', 620),
    tag('Hidden World', 'Settings', 'A supernatural society concealed from humans.', 480),
    tag('Immortality', 'Themes', 'Deathless life and its costs.', 390),
    tag('Bargain', 'Tropes', 'A binding supernatural deal.', 340),
  ],
  'horror-romance': [
    tag('Haunted House', 'Settings', 'A building that will not let go.', 380),
    tag('Monster', 'Characters', 'A genuinely frightening love interest.', 520),
    tag('Body Horror', 'Content Warnings', 'Bodily transformation and violation.', 340),
    tag('Dread', 'Themes', 'Sustained atmosphere over jump scares.', 420),
    tag('Cult', 'Settings', 'Ritual community as the threat.', 290),
    tag('Possession', 'Content Warnings', 'Loss of bodily autonomy.', 260),
    tag('Survival', 'Themes', 'Endurance as the emotional stake.', 380),
  ],
  'horror-erotica': [
    tag('Explicit', 'Content Warnings', 'Detailed intimate content throughout.', 1420),
    tag('Monster', 'Characters', 'A non-human, frightening partner.', 520),
    tag('Body Horror', 'Content Warnings', 'Transformation and bodily violation.', 340),
    tag('Dubious Consent', 'Content Warnings', 'Consent deliberately destabilised.', 440),
    tag('Possession', 'Content Warnings', 'Bodily autonomy overwritten.', 260),
    tag('Ritual', 'Settings', 'Occult ceremony as the setting for intimacy.', 210),
    tag('Tentacles', 'Content Warnings', 'Explicit non-human anatomy.', 260),
  ],
  'gothic-romance': [
    tag('Crumbling Estate', 'Settings', 'A decaying house heavy with history.', 340),
    tag('Brooding Love Interest', 'Characters', 'Withholding, haunted, magnetic.', 420),
    tag('Family Secret', 'Tropes', 'A buried truth shaping the present.', 480),
    tag('Isolation', 'Themes', 'Physical and social separation.', 390),
    tag('Ghost', 'Characters', 'A presence that may or may not be real.', 310),
    tag('Madness', 'Content Warnings', 'Sanity questioned, often the heroine’s.', 260),
    tag('Forbidden Wing', 'Settings', 'A part of the house kept locked.', 180),
  ],
}

/** Popular content tags for a subgenre, or an empty list when none are curated. */
export function getSubgenreContentTags(slug: string): SubgenreContentTag[] {
  return SUBGENRE_CONTENT_TAGS[slug] || []
}
